// src/contexts/ChatsDataContext.tsx
import { logger } from '../utils/logger';
// PERFORMANCE OPTIMIZATION: Preloads all Tab2 (Chats) data in the background
// This makes switching to the Chats tab instant instead of showing loading spinners

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import {
  getPendingRequests,
  getSentRequests,
  onConnectionUpdate,
  Connection
} from '../services/connectionsService';
import { getMyGroups, GroupWithDetails } from '../services/groupService';
import {
  getAllUnreadCounts,
  getAllGroupUnreadCounts,
  subscribeToUnreadChanges,
  UnreadCount,
  GroupUnreadCount
} from '../services/unreadService';
import { getBlockedUserIds } from '../services/blockProtectionService';

interface Match {
  id: string;
  otherUser: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
    avatar: any;
    bio: string;
    is_admin?: boolean;
  };
  lastMessage?: {
    content: string;
    created_at: string;
    sender_id: string;
  };
}

interface ChatsDataContextType {
  // Data
  matches: Match[];
  pendingRequests: Connection[];
  sentRequests: Connection[];
  groups: GroupWithDetails[];
  unreadCounts: UnreadCount[];
  groupUnreadCounts: GroupUnreadCount[];

  // Loading states
  loading: boolean;
  connectionsLoading: boolean;
  requestsLoading: boolean;
  groupsLoading: boolean;

  // Actions
  refresh: () => Promise<void>;
  refreshConnections: () => Promise<void>;
  refreshRequests: () => Promise<void>;
  refreshGroups: () => Promise<void>;
}

const ChatsDataContext = createContext<ChatsDataContextType | undefined>(undefined);

export const useChatsData = () => {
  const context = useContext(ChatsDataContext);
  if (!context) {
    throw new Error('useChatsData must be used within ChatsDataProvider');
  }
  return context;
};

export const ChatsDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [pendingRequests, setPendingRequests] = useState<Connection[]>([]);
  const [sentRequests, setSentRequests] = useState<Connection[]>([]);
  const [groups, setGroups] = useState<GroupWithDetails[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<UnreadCount[]>([]);
  const [groupUnreadCounts, setGroupUnreadCounts] = useState<GroupUnreadCount[]>([]);

  const [loading, setLoading] = useState(true);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [initialized, setInitialized] = useState(false);

  // Load connections/matches
  const loadConnections = useCallback(async (userId: string) => {
    setConnectionsLoading(true);
    try {
      // Get blocked user IDs first
      const { userIds: blockedUserIds } = await getBlockedUserIds();

      const { data: matchesData, error } = await supabase
        .from('matches')
        .select(`
          *,
          user1:profiles!matches_user1_id_fkey(id, username, display_name, avatar_url, meeple_avatar, bio, is_admin),
          user2:profiles!matches_user2_id_fkey(id, username, display_name, avatar_url, meeple_avatar, bio, is_admin)
        `)
        .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
        .eq('status', 'accepted')
        .order('updated_at', { ascending: false });

      if (!error && matchesData) {
        const matchesWithMessages = await Promise.all(
          matchesData.map(async (match: any) => {
            // Handle inconsistent Supabase relation responses (mobile platform fix)
            // Relations may return as arrays or single objects depending on platform
            const user1 = Array.isArray(match.user1)
              ? (match.user1.length > 0 ? match.user1[0] : null)
              : match.user1;
            const user2 = Array.isArray(match.user2)
              ? (match.user2.length > 0 ? match.user2[0] : null)
              : match.user2;

            // Skip if either user is null/undefined (data integrity check)
            if (!user1 || !user2) {
              logger.warn(`Skipping match ${match.id}: missing user data`, { user1, user2 });
              return null;
            }

            const otherUser = user1.id === userId ? user2 : user1;

            // Map meeple_avatar to avatar for application use
            if (otherUser) {
              otherUser.avatar = otherUser.meeple_avatar;
            }

            // Skip if other user is blocked
            if (otherUser && blockedUserIds.includes(otherUser.id)) {
              return null;
            }

            const { data: lastMsg } = await supabase
              .from('messages')
              .select('content, created_at, sender_id')
              .eq('match_id', match.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            return {
              id: match.id,
              otherUser: otherUser as any,
              lastMessage: lastMsg || undefined,
            };
          })
        );

        const processedMatches = matchesWithMessages
          .filter((m): m is NonNullable<typeof m> => m !== null && m.otherUser !== null) // Type guard to remove nulls
          .sort((a, b) => {
            const aTime = a.lastMessage?.created_at || '1970-01-01';
            const bTime = b.lastMessage?.created_at || '1970-01-01';
            return new Date(bTime).getTime() - new Date(aTime).getTime();
          });

        setMatches(processedMatches);
      }
    } catch (error) {
      logger.error('Error loading connections:', error);
    }
    setConnectionsLoading(false);
  }, []);

  // Load requests
  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const pendingResult = await getPendingRequests();
      const sentResult = await getSentRequests();

      setPendingRequests(pendingResult.success ? pendingResult.connections || [] : []);
      setSentRequests(sentResult.success ? (sentResult.connections as Connection[]) || [] : []);
    } catch (error) {
      logger.error('Error loading requests:', error);
    }
    setRequestsLoading(false);
  }, []);

  // Load groups
  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const result = await getMyGroups();
      setGroups(result.success && result.groups ? result.groups : []);
    } catch (error) {
      logger.error('Error loading groups:', error);
    }
    setGroupsLoading(false);
  }, []);

  // Load unread counts
  const loadUnreadCounts = useCallback(async () => {
    try {
      const directResult = await getAllUnreadCounts();
      const groupResult = await getAllGroupUnreadCounts();

      if (directResult.success && directResult.counts) {
        setUnreadCounts(directResult.counts);
      }
      if (groupResult.success && groupResult.counts) {
        setGroupUnreadCounts(groupResult.counts);
      }
    } catch (error) {
      logger.error('Error loading unread counts:', error);
    }
  }, []);

  // Refresh all data
  const refresh = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await Promise.all([
      loadConnections(user.id),
      loadRequests(),
      loadGroups(),
      loadUnreadCounts(),
    ]);
  }, [loadConnections, loadRequests, loadGroups, loadUnreadCounts]);

  // Individual refresh functions
  const refreshConnections = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await loadConnections(user.id);
  }, [loadConnections]);

  const refreshRequests = useCallback(async () => {
    await loadRequests();
  }, [loadRequests]);

  const refreshGroups = useCallback(async () => {
    await loadGroups();
  }, [loadGroups]);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      setCurrentUserId(user.id);

      // Load all data in parallel
      await Promise.all([
        loadConnections(user.id),
        loadRequests(),
        loadGroups(),
        loadUnreadCounts(),
      ]);

      setLoading(false);
      setInitialized(true);
    };

    init();
  }, [loadConnections, loadRequests, loadGroups, loadUnreadCounts]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!initialized || !currentUserId) return;

    // Subscribe to connection updates
    const unsubscribeConnections = onConnectionUpdate(() => {
      loadConnections(currentUserId);
      loadRequests();
    });

    // Subscribe to unread changes
    const unsubscribeUnread = subscribeToUnreadChanges(() => {
      loadUnreadCounts();
    });

    // Subscribe to matches table changes (for new messages)
    const matchesSubscription = supabase
      .channel('matches_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'matches',
          filter: `or(user1_id.eq.${currentUserId},user2_id.eq.${currentUserId})`
        },
        () => {
          loadConnections(currentUserId);
        }
      )
      .subscribe();

    // Subscribe to group changes
    const groupsSubscription = supabase
      .channel('groups_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'groups',
        },
        () => {
          loadGroups();
        }
      )
      .subscribe();

    return () => {
      unsubscribeConnections();
      unsubscribeUnread();
      matchesSubscription.unsubscribe();
      groupsSubscription.unsubscribe();
    };
  }, [initialized, currentUserId, loadConnections, loadRequests, loadGroups, loadUnreadCounts]);

  const value: ChatsDataContextType = {
    matches,
    pendingRequests,
    sentRequests,
    groups,
    unreadCounts,
    groupUnreadCounts,
    loading,
    connectionsLoading,
    requestsLoading,
    groupsLoading,
    refresh,
    refreshConnections,
    refreshRequests,
    refreshGroups,
  };

  return (
    <ChatsDataContext.Provider value={value}>
      {children}
    </ChatsDataContext.Provider>
  );
};